import { friendlyError } from '../../utils/userMessages';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Avatar, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack, AutoAwesome, Business, Inventory2 as Category, Chat, Close, Email, Language,
  LocationOn, Person, Send, ShoppingBagRounded as ShoppingBag, BadgeOutlined as Work, WorkspacePremium,
} from '@mui/icons-material';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Header from '../common/Header/Header';
import LoadingSpinner from '../common/LoadingSpinner';
import { db } from '../../config/firebase';
import { useAuth } from '../auth/AuthContext';
import { resolveCompanyId, getPersonPhotoUrl, getCompanyCoverUrl, getCompanyLogoUrl } from '../../utils/companyIdentity';
import { brandPalette as palette } from '../../utils/brandPalette';
import { analyzeMatch, generateIntroduction } from '../../services/integrations/ai';
import { introductionService } from '../../services/workflow/communication';
import { userBusinessProfile } from '../../utils/businessProfile';
import { buildBusinessIntroductionDraft } from '../../utils/aiFallback';
import EntityRatingButton from '../common/EntityRatingButton';

const txt = (value) => String(value || '').trim();
const fullName = (u) => txt(u?.displayName || u?.name || [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || 'TELCOFAIR User');

const InfoTile = ({ icon, label, value, color = '#2F80ED' }) => (
  <Box className="tf-profile-info-tile" sx={{ '--tile-color': color }}>
    <Box className="tf-profile-info-icon">{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography className="tf-profile-info-label">{label}</Typography>
      <Typography className="tf-profile-info-value" noWrap>{value || 'Not specified'}</Typography>
    </Box>
  </Box>
);

const PillList = ({ title, items, color, icon }) => (
  <Paper elevation={0} className="tf-profile-section-card" sx={{ '--section-color': color }}>
    <Stack direction="row" spacing={1.1} sx={{ alignItems: 'center', mb: 1.3 }}>
      <Box className="tf-profile-section-icon">{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 900, color: '#182235', fontSize: 14 }}>{title}</Typography>
        <Typography sx={{ fontSize: 10.5, color: '#7A899C' }}>{items.length ? `${items.length} listed` : 'No details added yet'}</Typography>
      </Box>
    </Stack>
    <Stack direction="row" spacing={.7} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {items.length ? items.map((item) => <Chip key={item} label={item} size="small" sx={{ bgcolor: `${color}10`, color, border: `1px solid ${color}1E`, fontWeight: 750 }} />) : <Typography sx={{ fontSize: 12, color: '#8A98A8' }}>Not specified</Typography>}
    </Stack>
  </Paper>
);

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData, companyData } = useAuth();
  const ownCompanyId = resolveCompanyId({ currentUser, userData, companyData });
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [permission, setPermission] = useState(null);
  const [businessMatch, setBusinessMatch] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'users', id));
        if (!snap.exists()) throw new Error('User profile not found');
        const record = { id: snap.id, ...snap.data() };
        if (!alive) return;
        setProfile(record);

        const targetCompanyId = record.companyId || record.company?.id || '';
        if (targetCompanyId) {
          const companySnap = await getDoc(doc(db, 'companies', targetCompanyId));
          if (alive && companySnap.exists()) setCompany({ id: companySnap.id, ...companySnap.data() });
        } else if (record.companyName) {
          try {
            const companySnap = await getDocs(query(collection(db, 'companies'), where('companyName', '==', record.companyName)));
            if (alive && !companySnap.empty) {
              const companyDoc = companySnap.docs[0];
              setCompany({ id: companyDoc.id, ...companyDoc.data() });
            }
          } catch (companyLookupError) {
            console.warn('Legacy company lookup failed:', companyLookupError);
          }
        }

        if (ownCompanyId && id !== currentUser?.uid) {
          try {
            const p = await introductionService.permission(ownCompanyId, id);
            if (alive) setPermission(p || null);
          } catch (e) {
            console.warn('Interaction permission unavailable:', e);
          }
        }
        if (ownCompanyId && id !== currentUser?.uid) {
          try {
            const analysis = await analyzeMatch({ companyId: ownCompanyId, targetUserId: id, targetCompanyId: record.companyId || '' });
            if (alive) setBusinessMatch(analysis?.matchAnalysis || null);
          } catch (matchError) {
            console.warn('Business match analysis unavailable:', matchError);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error(friendlyError(error, 'Unable to load user profile.'));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [id, ownCompanyId, currentUser?.uid]);

  const business = useMemo(() => userBusinessProfile(profile || {}, company || {}), [profile, company]);
  const businessAreas = useMemo(() => business.specialization, [business]);
  const offers = useMemo(() => [...business.primaryServices, ...business.secondaryServices], [business]);
  const needs = useMemo(() => [...business.primaryNeeds, ...business.secondaryNeeds], [business]);

  const prepareIntro = async () => {
    if (!profile) return;
    if (!ownCompanyId) return toast.error('Your company workspace is still loading.');
    setIntroOpen(true);
    setAiLoading(true);
    setDraft('');
    try {
      const result = await generateIntroduction({
        companyId: ownCompanyId,
        targetUserId: profile.id,
        targetCompanyId: profile.companyId || company?.id || '',
        context: {
          recipientName: fullName(profile),
          recipientTitle: profile.jobTitle || profile.designation || '',
          recipientCompany: profile.companyName || company?.companyName || '',
          recipientOffers: offers,
          recipientNeeds: needs,
          source: 'user_profile',
        },
      });
      const resolvedMatch = result?.matchAnalysis || businessMatch || null;
      if (result?.matchAnalysis) setBusinessMatch(result.matchAnalysis);
      setDraft(result?.text || buildBusinessIntroductionDraft({ senderUser: userData || {}, targetUser: profile, senderCompany: companyData || {}, targetCompany: company || {}, match: resolvedMatch }));
    } catch (error) {
      console.error(error);
      setDraft(buildBusinessIntroductionDraft({ senderUser: userData || {}, targetUser: profile, senderCompany: companyData || {}, targetCompany: company || {}, match: businessMatch || null }));
      toast.error('ChatGPT is temporarily unavailable. A business-fit draft was generated locally so you can still review and send it.', { duration: 6000 });
    } finally {
      setAiLoading(false);
    }
  };

  const sendIntro = async () => {
    if (!draft.trim() || !profile) return;
    setSending(true);
    try {
      await introductionService.send(ownCompanyId, {
        targetUserId: profile.id,
        targetCompanyId: profile.companyId || company?.id || '',
        message: draft.trim(),
        context: { source: 'user_profile' },
      });
      toast.success(`Introduction sent to ${fullName(profile)}. They control whether chat or calendar access is enabled.`);
      setIntroOpen(false);
      setDraft('');
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to send introduction.'));
    } finally {
      setSending(false);
    }
  };


  useEffect(() => {
    if (!loading && profile && location.state?.openAiIntroduction && profile.id !== currentUser?.uid) {
      prepareIntro();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // One-shot bridge for the Company People -> AI Intro action.
  }, [loading, profile?.id, location.state?.openAiIntroduction]);

  if (loading) return <Box className="erp-page-shell erp-internal-page"><Header /><LoadingSpinner message="Loading professional profile…" /></Box>;
  if (!profile) return <Box className="erp-page-shell erp-internal-page"><Header /><Container sx={{ py: 4 }}><Paper className="tf-profile-section-card" sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontWeight: 900 }}>Profile unavailable</Typography><Button onClick={() => navigate('/dashboard/users')} sx={{ mt: 2 }}>Back to Directory</Button></Paper></Container></Box>;

  const name = fullName(profile);
  const companyName = txt(profile.companyName || company?.companyName || 'Independent Professional');
  const title = txt(profile.jobTitle || profile.designation || profile.seniorityLevel || 'Business Professional');
  const country = txt(profile.country || profile.countryName || company?.country || '');
  const photo = getPersonPhotoUrl(profile);
  const hasCompanyEntity = Boolean(company && (company.id || Object.keys(company).length));
  const companyCover = hasCompanyEntity ? getCompanyCoverUrl(company) : (profile.companyCoverImage || profile.companyCoverUrl || '');
  const companyLogo = hasCompanyEntity ? getCompanyLogoUrl(company) : (profile.companyLogo || profile.companyLogoUrl || '');
  const canChat = Boolean(permission?.canChat || permission?.chatEnabled || permission?.permission?.canChat);
  const isSelf = profile.id === currentUser?.uid;

  return (
    <Box className="erp-page-shell erp-internal-page" sx={{ minHeight: '100vh' }}>
      <Header />
      <Container maxWidth={false} sx={{ maxWidth: 1550, py: { xs: 2, md: 2.5 }, px: { xs: 1.5, sm: 2.5, md: 3 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.4 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard/users')} sx={{ color: '#526177', fontWeight: 800, textTransform: 'none' }}>User Directory</Button>
          {!isSelf && <Chip icon={<WorkspacePremium />} label={permission?.status || (canChat ? 'Chat enabled' : 'Recipient controlled')} size="small" sx={{ fontWeight: 800, bgcolor: canChat ? 'rgba(19,167,115,.09)' : 'rgba(var(--primary-rgb),.07)', color: canChat ? '#0D7A56' : 'var(--primary-ink)' }} />}
        </Stack>

        <Paper elevation={0} className="tf-profile-hero" sx={companyCover ? { minHeight: 250, pt: { xs: 10, md: 12 }, backgroundImage: `linear-gradient(180deg,rgba(20,36,52,.08),rgba(20,36,52,.28) 46%,rgba(255,255,255,.97) 46%,rgba(255,255,255,.99) 100%), url("${companyCover}") !important`, backgroundSize: 'cover !important', backgroundPosition: 'center !important' } : undefined}>
          <Box className="tf-profile-hero-art" />
          <Grid container spacing={2.2} sx={{ alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Box sx={{ position: 'relative', width: { xs: 86, md: 108 }, height: { xs: 86, md: 108 } }}>
                <Avatar src={photo} sx={{ width: { xs: 82, md: 104 }, height: { xs: 82, md: 104 }, borderRadius: '25px', fontSize: 34, fontWeight: 900, color: 'var(--brand-action-text,#fff)', background: 'linear-gradient(145deg,var(--primary),var(--secondary))', border: '4px solid rgba(255,255,255,.86)', boxShadow: '0 18px 40px rgba(var(--primary-rgb),.24)' }}>{name[0]}</Avatar>
                {companyLogo && <Avatar src={companyLogo} variant="rounded" sx={{ position: 'absolute', right: 0, bottom: 0, width: 29, height: 29, borderRadius: '8px', bgcolor: '#fff', border: '3px solid #fff', boxShadow: '0 6px 14px rgba(25,57,78,.16)', '& img': { objectFit: 'contain' } }} />}
              </Box>
            </Grid>
            <Grid  size={{ xs: 12, md: 'grow' }}>
              <Stack direction="row" spacing={.8} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: { xs: 25, md: 31 }, fontWeight: 950, color: '#142034', letterSpacing: '-.04em' }}>{name}</Typography>
                {profile.isVerified && <Chip size="small" label="Verified" sx={{ bgcolor: 'rgba(19,167,115,.09)', color: '#0E7C57', fontWeight: 850 }} />}
              </Stack>
              <Typography sx={{ mt: .35, fontSize: 14, color: '#526177', fontWeight: 750 }}>{title}</Typography>
              <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.1 }}>
                <Chip icon={<Business />} label={companyName} onClick={company?.id || profile.companyId ? () => navigate(`/dashboard/company/${company?.id || profile.companyId}`) : undefined} sx={{ bgcolor: 'rgba(255,255,255,.68)', border: '1px solid rgba(31,62,82,.10)', fontWeight: 800 }} />
                {country && <Chip icon={<LocationOn />} label={country} sx={{ bgcolor: 'rgba(255,255,255,.68)', border: '1px solid rgba(31,62,82,.10)', fontWeight: 800 }} />}
                {profile.seniorityLevel && <Chip icon={<Work />} label={profile.seniorityLevel} sx={{ bgcolor: 'rgba(255,255,255,.68)', border: '1px solid rgba(31,62,82,.10)', fontWeight: 800 }} />}
              </Stack>
            </Grid>
            {!isSelf && <Grid size={{ xs: 12, md: 'auto' }}>
              <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} sx={{ minWidth: { md: 190 } }}>
                <Button fullWidth variant="contained" startIcon={<AutoAwesome />} onClick={prepareIntro} sx={{ minHeight: 42, borderRadius: '12px', textTransform: 'none', fontWeight: 900, background: 'linear-gradient(135deg,var(--primary),var(--secondary))', color: 'var(--brand-action-text)', boxShadow: '0 12px 25px rgba(var(--primary-rgb),.22)' }}>Send My Introduction</Button>
                <Tooltip title={canChat ? 'Open direct chat' : 'Chat becomes available only after this recipient enables it'}>
                  <span><Button fullWidth disabled={!canChat} startIcon={<Chat />} onClick={() => navigate('/dashboard/messages', { state: { userId: profile.id, userName: name } })} sx={{ minHeight: 40, borderRadius: '12px', textTransform: 'none', fontWeight: 850, bgcolor: '#fff', border: '1px solid rgba(31,62,82,.11)', color: '#314057' }}>Chat</Button></span>
                </Tooltip>
                <EntityRatingButton targetType="user" targetId={profile.id} targetName={name} target={profile} color="#E9A220" />
              </Stack>
            </Grid>}
          </Grid>
        </Paper>

        {!isSelf && <Paper elevation={0} className="tf-profile-match-command" sx={{ '--match-color': businessMatch?.score >= 70 ? '#13A773' : businessMatch?.score >= 45 ? '#2F6FED' : '#F39A24' }}>
          <Box className="tf-profile-match-art"><AutoAwesome /></Box>
          <Grid container spacing={1.4} sx={{ alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <Grid size={{ xs: 12, md: 'auto' }}><Box className="tf-profile-match-score"><Typography sx={{ fontSize: 28, lineHeight: 1, fontWeight: 950 }}>{Math.round(Number(businessMatch?.score || 0))}%</Typography><Typography sx={{ mt: .25, fontSize: 8.5, fontWeight: 900, letterSpacing: '.06em' }}>BUSINESS FIT</Typography></Box></Grid>
            <Grid  size={{ xs: 12, md: 'grow' }}><Typography sx={{ fontSize: 14, fontWeight: 950, color: '#1C2A3D' }}>{businessMatch?.level || 'AI business compatibility'}</Typography><Typography sx={{ mt: .25, fontSize: 10.7, color: '#718096' }}>TELCOFAIR compares specialization, primary & secondary services, service regions and both sides’ buying needs before ChatGPT writes the introduction.</Typography><Stack direction="row" spacing={.55} useFlexGap sx={[{ flexWrap: 'wrap' }, { mt: .75 }]}>{(businessMatch?.reasons || []).slice(0, 3).map((reason, i) => <Chip key={i} size="small" icon={<AutoAwesome />} label={reason} sx={{ height: 24, maxWidth: 330, bgcolor: 'rgba(255,255,255,.72)', border: '1px solid rgba(31,62,82,.08)', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />)}</Stack></Grid>
            <Grid size={{ xs: 12, md: 'auto' }}><Button variant="contained" startIcon={<AutoAwesome />} onClick={prepareIntro} sx={{ minHeight: 38, fontWeight: 900 }}>Generate Matched Intro</Button></Grid>
          </Grid>
        </Paper>}

        <Grid container spacing={1.5} sx={{ mt: .1 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><InfoTile icon={<Person fontSize="small" />} label="Professional Role" value={title} color="#7C5CF2" /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><InfoTile icon={<Business fontSize="small" />} label="Company" value={companyName} color="#2F80ED" /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><InfoTile icon={<LocationOn fontSize="small" />} label="Country" value={country || 'Not specified'} color="#13A773" /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><InfoTile icon={<Language fontSize="small" />} label="Business Area" value={businessAreas[0] || 'Not specified'} color="#F39A24" /></Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper elevation={0} className="tf-profile-section-card" sx={{ '--section-color': '#2F80ED', height: '100%' }}>
              <Stack direction="row" spacing={1.1} sx={{ alignItems: 'center', mb: 1.2 }}><Box className="tf-profile-section-icon"><Person /></Box><Box><Typography sx={{ fontWeight: 900, color: '#182235' }}>Professional Overview</Typography><Typography sx={{ fontSize: 10.5, color: '#7A899C' }}>Business profile and matchmaking context</Typography></Box></Stack>
              <Typography sx={{ color: '#596A80', fontSize: 12.8, lineHeight: 1.75 }}>{txt(profile.bio || profile.about || profile.professionalSummary || profile.businessDescription || 'This member has not added a detailed professional summary yet.')}</Typography>
              <Divider sx={{ my: 1.6 }} />
              <Grid container spacing={1.2}>
                <Grid size={{ xs: 12, sm: 6 }}><InfoTile icon={<Work fontSize="small" />} label="Job Title" value={title} color="#7C5CF2" /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><InfoTile icon={<WorkspacePremium fontSize="small" />} label="Seniority" value={profile.seniorityLevel || profile.seniority || 'Not specified'} color="#C047A8" /></Grid>
                {profile.publicEmail && <Grid size={{ xs: 12, sm: 6 }}><InfoTile icon={<Email fontSize="small" />} label="Public Email" value={profile.publicEmail} color="#E84B7A" /></Grid>}
                {profile.website && <Grid size={{ xs: 12, sm: 6 }}><InfoTile icon={<Language fontSize="small" />} label="Website" value={profile.website} color="#0F9E9A" /></Grid>}
              </Grid>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={1.5}>
              <PillList title="Business Areas" items={businessAreas} color="#7C5CF2" icon={<Category />} />
              <Paper elevation={0} className="tf-profile-section-card" sx={{ '--section-color': '#13A773' }}>
                <Typography sx={{ fontWeight: 900, color: '#182235', mb: .4 }}>Communication Control</Typography>
                <Typography sx={{ fontSize: 11.5, lineHeight: 1.6, color: '#748397' }}>{canChat ? 'This recipient has enabled chat for your interaction. You may continue the conversation from Messages.' : 'You may send an introduction. The recipient alone decides whether to enable direct chat or share meeting availability.'}</Typography>
              </Paper>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}><PillList title="Primary Services" items={business.primaryServices} color="#13A773" icon={<Category />} /></Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}><PillList title="Secondary Services" items={business.secondaryServices} color="#2F6FED" icon={<Category />} /></Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}><PillList title="Service Regions" items={[...business.primaryRegions, ...business.secondaryRegions]} color="#8B4DE8" icon={<LocationOn />} /></Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}><PillList title="Looking For" items={[...business.primaryNeeds, ...business.secondaryNeeds]} color="#F39A24" icon={<ShoppingBag />} /></Grid>
        </Grid>
      </Container>

      <Dialog open={introOpen} onClose={() => !sending && setIntroOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { className: 'tf-premium-dialog' } }}>
        <DialogTitle sx={{ fontWeight: 950, color: '#182235', pr: 6 }}>Send My Introduction<IconButton onClick={() => setIntroOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}><Close /></IconButton></DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.4}>
            <Stack direction="row" spacing={1.1} sx={{ alignItems: 'center' }}><Avatar src={photo}>{name[0]}</Avatar><Box><Typography sx={{ fontWeight: 900 }}>{name}</Typography><Typography sx={{ fontSize: 11.5, color: '#748397' }}>{title} · {companyName}</Typography></Box><Box sx={{ ml: 'auto', textAlign: 'right' }}><Typography sx={{ fontSize: 18, fontWeight: 950, color: businessMatch?.score >= 70 ? '#13A773' : '#2F6FED' }}>{Math.round(Number(businessMatch?.score || 0))}% Match</Typography><Typography sx={{ fontSize: 9.5, color: '#8A98A8' }}>{businessMatch?.level || 'Business fit'}</Typography></Box></Stack>
            {businessMatch?.reasons?.length ? <Stack direction="row" spacing={.55} useFlexGap sx={{ flexWrap: 'wrap' }}>{businessMatch.reasons.slice(0,3).map((r,i)=><Chip key={i} size="small" label={r} sx={{ height:24,fontSize:9.7,bgcolor:'rgba(47,111,237,.06)' }} />)}</Stack> : null}
            {aiLoading ? <Stack direction="row" spacing={1} sx={[{ alignItems: 'center', justifyContent: 'center' }, { py: 5 }]}><CircularProgress size={22} /><Typography sx={{ fontSize: 12.5 }}>ChatGPT is preparing a contextual introduction…</Typography></Stack> : <TextField fullWidth multiline minRows={8} value={draft} onChange={(e) => setDraft(e.target.value)} label="Editable AI introduction" helperText="Review and edit before sending. The recipient controls Chat / Share Calendar / Ignore." />}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setIntroOpen(false)}>Cancel</Button><Button variant="contained" startIcon={sending ? <CircularProgress size={15} color="inherit" /> : <Send />} disabled={sending || aiLoading || !draft.trim()} onClick={sendIntro} sx={{ fontWeight: 900, borderRadius: '10px' }}>{sending ? 'Sending…' : 'Send Introduction'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
